import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Check, Users, MapPin, ScanFace, Sparkles, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function UsageBar({ icon: Icon, label, used, limit, tint }) {
  const pct = limit == null ? 30 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={`usage-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-slate-600">
        <Icon className={`h-4 w-4 ${tint}`} />
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <p className="mt-3 text-2xl font-head font-black text-slate-900">
        {used}
        <span className="text-base font-semibold text-slate-400"> / {limit == null ? "∞" : limit}</span>
      </p>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Billing() {
  const { data: status, isLoading, refetch } = useQuery({ queryKey: ["billing-status"], queryFn: () => api.get("/billing/status") });
  const { data: plansData } = useQuery({ queryKey: ["billing-plans"], queryFn: () => api.get("/billing/plans") });

  const checkout = useMutation({
    mutationFn: (plan) => api.post("/billing/checkout", { plan }),
    onSuccess: (r) => { if (r.url) window.location.href = r.url; },
    onError: (e) => toast.error(apiError(e)),
  });
  const portal = useMutation({
    mutationFn: () => api.post("/billing/portal"),
    onSuccess: (r) => { if (r.url) window.location.href = r.url; },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading || !status) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const plans = plansData?.plans || [];
  const currentPlan = status.plan;
  const u = status.usage || {};
  const lim = status.limits || {};

  return (
    <div className="space-y-6 animate-fade-up" data-testid="billing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900 rounded-xl p-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Current Plan</p>
          <p className="font-head text-2xl font-extrabold text-white capitalize mt-1">{currentPlan}</p>
          <p className="text-sm text-slate-400 mt-1">Status: <span className="capitalize text-emerald-400 font-semibold">{status.status}</span></p>
        </div>
        {status.stripe_configured && (
          <button onClick={() => portal.mutate()} disabled={portal.isPending} data-testid="billing-portal-btn"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition">
            {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Manage Subscription
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <UsageBar icon={Users} label="Employees" used={u.employees ?? 0} limit={lim.max_employees} tint="text-indigo-500" />
        <UsageBar icon={MapPin} label="Sites" used={u.sites ?? 0} limit={lim.max_sites} tint="text-sky-500" />
        <UsageBar icon={ScanFace} label="AI Audits" used={u.ai_audits_month ?? 0} limit={lim.monthly_ai_audits} tint="text-emerald-500" />
      </div>

      {!status.stripe_configured && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="stripe-warning">
          <Sparkles className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p>Stripe is not configured yet. Add your Stripe keys &amp; price IDs to the backend to enable live checkout. Plan cards below are for preview.</p>
        </div>
      )}

      <div>
        <h3 className="font-head text-lg font-bold text-slate-900 mb-4">Available Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => {
            const active = p.id === currentPlan;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative bg-white border rounded-2xl shadow-sm p-6 transition-all ${active ? "border-indigo-500 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"}`}>
                {active && <span className="absolute -top-3 left-6 text-xs font-bold px-3 py-1 rounded-full bg-indigo-600 text-white">CURRENT</span>}
                {p.id === "pro" && !active && <span className="absolute -top-3 left-6 text-xs font-bold px-3 py-1 rounded-full bg-slate-900 text-white">POPULAR</span>}
                <h4 className="font-head text-xl font-extrabold text-slate-900">{p.name}</h4>
                <p className="mt-2 flex items-baseline gap-1">
                  <span className="font-head text-3xl font-black text-slate-900">${p.price}</span>
                  <span className="text-sm text-slate-400">/mo</span>
                </p>
                <ul className="mt-5 space-y-2.5 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.max_employees ?? "Unlimited"} employees</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.max_sites ?? "Unlimited"} site(s)</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.monthly_ai_audits ?? "Unlimited"} AI audits/mo</li>
                </ul>
                <button
                  onClick={() => checkout.mutate(p.id)}
                  disabled={active || checkout.isPending}
                  data-testid={`plan-select-${p.id}`}
                  className={`mt-6 w-full h-11 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 ${
                    active ? "bg-slate-100 text-slate-400 cursor-default" : "bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}>
                  {checkout.isPending && checkout.variables === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {active ? "Current Plan" : "Choose Plan"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
